import { useEffect, useMemo, useRef, useState } from 'react'

import type { UserPreferences } from '@study-decks/shared'

import { Button } from '../Button'
import {
  DEFAULT_PREFERENCES,
  applyPreferencesToDocument,
  listenToPreferences,
  updatePreferences,
} from '../../lib/preferences.js'
import styles from './PreferencesPanel.module.css'

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

interface PreferencesPanelProps {
  open: boolean
  uid: string | null
  onClose: () => void
}

const FONT_SIZES: UserPreferences['fontSize'][] = ['sm', 'md', 'lg', 'xl']

function saveStateText(saveState: SaveState) {
  if (saveState === 'saving') return 'Saving...'
  if (saveState === 'saved') return 'Saved'
  if (saveState === 'error') return "Couldn't save - retry"
  return 'Saved'
}

function voiceKey(voice: SpeechSynthesisVoice) {
  return `${voice.name} (${voice.lang})`
}

function findVoice(voices: SpeechSynthesisVoice[], key: string | null) {
  if (!key) return null
  return voices.find((voice) => voiceKey(voice) === key || voice.name === key) ?? null
}

function fontSizeFromRange(value: string): UserPreferences['fontSize'] {
  return FONT_SIZES[Number(value)] ?? 'md'
}

export function PreferencesPanel({ open, uid, onClose }: PreferencesPanelProps): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement | null>(null)
  const loadedRef = useRef(false)
  const [draft, setDraft] = useState<UserPreferences>(DEFAULT_PREFERENCES)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) {
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  useEffect(() => {
    if (!uid) {
      loadedRef.current = false
      setDraft(DEFAULT_PREFERENCES)
      applyPreferencesToDocument(DEFAULT_PREFERENCES)
      return
    }

    const unsubscribe = listenToPreferences(uid, (preferences) => {
      loadedRef.current = false
      setDraft(preferences)
      applyPreferencesToDocument(preferences)
      window.setTimeout(() => {
        loadedRef.current = true
      }, 0)
    })
    return unsubscribe
  }, [uid])

  useEffect(() => {
    if (!uid || !loadedRef.current) return
    applyPreferencesToDocument(draft)
    setSaveState('saving')
    const timer = window.setTimeout(() => {
      void updatePreferences(uid, draft)
        .then((preferences) => {
          setDraft(preferences)
          applyPreferencesToDocument(preferences)
          setSaveState('saved')
        })
        .catch(() => {
          setSaveState('error')
        })
    }, 400)
    return () => window.clearTimeout(timer)
  }, [draft, uid])

  useEffect(() => {
    function loadVoices() {
      if (!('speechSynthesis' in window)) return
      setVoices(window.speechSynthesis.getVoices())
    }
    loadVoices()
    if (!('speechSynthesis' in window)) return
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices)
    return () => window.speechSynthesis.removeEventListener('voiceschanged', loadVoices)
  }, [])

  const selectedVoice = useMemo(() => findVoice(voices, draft.ttsVoice), [draft.ttsVoice, voices])

  function patchPreferences(patch: Partial<UserPreferences>) {
    setDraft((previous) => ({ ...previous, ...patch }))
  }

  function testVoice() {
    if (!('speechSynthesis' in window)) return
    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance('The quick brown fox jumps over the lazy dog.')
    utterance.rate = draft.ttsRate
    if (selectedVoice) utterance.voice = selectedVoice
    window.speechSynthesis.speak(utterance)
  }

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby="preferences-title"
      onCancel={onClose}
      onClose={onClose}
    >
      <form method="dialog" className={styles.panel}>
        <header className={styles.header}>
          <div>
            <p className={styles.eyebrow}>Display & accessibility</p>
            <h2 id="preferences-title">Study preferences</h2>
          </div>
          <Button type="submit" variant="secondary">
            Close
          </Button>
        </header>

        <div className={styles.grid}>
          <label className={styles.field}>
            <span>Font family</span>
            <select
              value={draft.font}
              onChange={(event) =>
                patchPreferences({ font: event.currentTarget.value as UserPreferences['font'] })
              }
            >
              <option value="system">System default</option>
              <option value="lexend">Lexend</option>
              <option value="atkinson">Atkinson Hyperlegible</option>
              <option value="opendyslexic">OpenDyslexic</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>Font size</span>
            <input
              type="range"
              min="0"
              max="3"
              step="1"
              value={FONT_SIZES.indexOf(draft.fontSize)}
              aria-valuetext={draft.fontSize.toUpperCase()}
              onChange={(event) =>
                patchPreferences({ fontSize: fontSizeFromRange(event.currentTarget.value) })
              }
            />
            <span className={styles.rangeLabels} aria-hidden="true">
              <span>Small</span>
              <span>Medium</span>
              <span>Large</span>
              <span>XL</span>
            </span>
          </label>

          <fieldset className={styles.fieldset}>
            <legend>Contrast</legend>
            <label>
              <input
                type="radio"
                name="contrast"
                value="standard"
                checked={draft.contrast === 'standard'}
                onChange={() => patchPreferences({ contrast: 'standard' })}
              />
              Standard
            </label>
            <label>
              <input
                type="radio"
                name="contrast"
                value="high"
                checked={draft.contrast === 'high'}
                onChange={() => patchPreferences({ contrast: 'high' })}
              />
              High
            </label>
          </fieldset>

          <label className={styles.checkField}>
            <input
              type="checkbox"
              checked={draft.reduceMotion}
              onChange={(event) => patchPreferences({ reduceMotion: event.currentTarget.checked })}
            />
            Reduce motion
          </label>

          <label className={styles.field}>
            <span>TTS voice</span>
            <select
              value={draft.ttsVoice ?? ''}
              onChange={(event) =>
                patchPreferences({ ttsVoice: event.currentTarget.value || null })
              }
            >
              <option value="">System default</option>
              {voices.map((voice) => (
                <option key={voiceKey(voice)} value={voiceKey(voice)}>
                  {voice.name} ({voice.lang})
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span>TTS rate: {draft.ttsRate.toFixed(1)}x</span>
            <input
              type="range"
              min="0.5"
              max="1.5"
              step="0.1"
              value={draft.ttsRate}
              onChange={(event) => patchPreferences({ ttsRate: Number(event.currentTarget.value) })}
            />
          </label>
        </div>

        <footer className={styles.footer}>
          <Button type="button" variant="secondary" onClick={testVoice}>
            Test voice
          </Button>
          <p role="status" aria-live="polite" className={styles.saveState}>
            {saveStateText(saveState)}
          </p>
        </footer>
      </form>
    </dialog>
  )
}
